import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getBalanceSummary, getBalanceHistory, redeemPromoCode, setBalanceAutoApply } from '../api/client';
import type { BalanceSummary, LedgerEntryResponse } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Gift, ShoppingBag } from 'lucide-react';

function formatUSD(amount: number): string {
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function BalancePage() {
  useDocumentTitle('Snagg Balance');
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [autoApply, setAutoApply] = useState(true);
  const [balance, setBalance] = useState<BalanceSummary | null>(null);
  const [history, setHistory] = useState<LedgerEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemMessage, setRedeemMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadData();
    if (user) setAutoApply(user.useBalanceForPurchases !== false);
  }, [user]);

  function handleAutoApplyToggle() {
    const next = !autoApply;
    setAutoApply(next);
    setBalanceAutoApply(next).then(() => refreshUser().catch(() => {})).catch(() => setAutoApply(!next));
  }

  async function loadData() {
    try {
      const [bal, hist] = await Promise.all([
        getBalanceSummary(),
        getBalanceHistory(0, 50).then(d => d.content || []).catch(() => []),
      ]);
      setBalance(bal);
      setHistory(hist);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  async function handleRedeem() {
    if (!code.trim()) return;
    setRedeemLoading(true);
    setRedeemMessage(null);
    try {
      const updated = await redeemPromoCode(code.trim());
      setBalance(updated);
      setRedeemMessage({ type: 'success', text: `Added to your Snagg Balance!` });
      setCode('');
      loadData();
    } catch (e: unknown) {
      setRedeemMessage({ type: 'error', text: (e as Error).message || 'Invalid code' });
    } finally {
      setRedeemLoading(false);
    }
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button style={styles.backButton} onClick={() => navigate('/account')}>
            <ArrowLeft size={18} /> Back to Account
          </button>

          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : (
            <>
              {/* Hero Card */}
              <div style={styles.heroCard}>
                <div style={styles.heroLabel}>SNAGG BALANCE</div>
                <div style={styles.heroAmount}>
                  ${formatUSD(balance?.availableBalance ?? 0)}
                </div>
                <div style={styles.heroSub}>
                  <span>Available now</span>
                  {balance && balance.pendingBalance > 0 && (
                    <span>${formatUSD(balance.pendingBalance)} pending</span>
                  )}
                </div>
                <div style={styles.autoApplyRow}>
                  <span style={styles.autoApplyLabel}>Automatically apply towards my next purchase</span>
                  <button onClick={handleAutoApplyToggle} style={{
                    width: 36, height: 20, borderRadius: 10, border: 'none',
                    background: autoApply ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                    position: 'relative', cursor: 'pointer', flexShrink: 0,
                    transition: 'background 200ms',
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: autoApply ? '#5F3DC4' : 'rgba(255,255,255,0.6)',
                      position: 'absolute', top: 2,
                      left: autoApply ? 18 : 2,
                      transition: 'left 200ms',
                    }} />
                  </button>
                </div>
              </div>

              {/* Promo Code Entry */}
              <div style={styles.promoSection}>
                <div style={styles.promoLabel}>HAVE A PROMO CODE?</div>
                <div style={styles.promoRow}>
                  <input
                    style={styles.promoInput}
                    placeholder="Enter code"
                    value={code}
                    onChange={e => { setCode(e.target.value); setRedeemMessage(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleRedeem()}
                  />
                  <button
                    style={{ ...styles.promoBtn, opacity: !code.trim() || redeemLoading ? 0.5 : 1 }}
                    disabled={!code.trim() || redeemLoading}
                    onClick={handleRedeem}
                  >
                    {redeemLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {redeemMessage && (
                  <div style={{
                    fontSize: 13,
                    marginTop: 8,
                    color: redeemMessage.type === 'success' ? '#22C55E' : '#DC2626',
                    fontWeight: 500,
                  }}>
                    {redeemMessage.text}
                  </div>
                )}
              </div>

              {/* Transaction History */}
              <div style={styles.historySection}>
                <div style={styles.historyLabel}>TRANSACTION HISTORY</div>
                {history.length === 0 ? (
                  <div style={styles.emptyHistory}>No transactions yet</div>
                ) : (
                  <div style={styles.historyList}>
                    {history.map(entry => (
                      <div key={entry.id} style={styles.historyRow}>
                        <div style={{
                          ...styles.historyIcon,
                          background: entry.entryType === 'CREDIT' ? 'rgba(34,197,94,0.1)' : colors.gray[100],
                          color: entry.entryType === 'CREDIT' ? '#1f7a4d' : colors.gray[600],
                        }}>
                          {entry.entryType === 'CREDIT' ? <Gift size={16} /> : <ShoppingBag size={16} />}
                        </div>
                        <div style={styles.historyBody}>
                          <div style={styles.historyDesc}>{entry.description}</div>
                          <div style={styles.historyDate}>
                            {new Date(entry.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{
                          ...styles.historyAmount,
                          color: entry.entryType === 'CREDIT' ? '#1f7a4d' : colors.ink.black,
                        }}>
                          {entry.entryType === 'CREDIT' ? '+' : '-'}${formatUSD(entry.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '24px' },
  content: { maxWidth: '600px', margin: '0 auto' },
  backButton: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'none', border: 'none', cursor: 'pointer',
    color: colors.gray[600], fontSize: 14, fontWeight: 500,
    marginBottom: 16, padding: 0,
  },
  title: { fontSize: 28, fontWeight: 700, color: colors.ink.black, marginBottom: 24 },
  loading: { padding: 40, textAlign: 'center', color: colors.gray[600] },
  heroCard: {
    padding: '28px 24px',
    borderRadius: 20,
    background: 'linear-gradient(135deg, #5F3DC4 0%, #CD3C9C 100%)',
    color: '#fff',
    marginBottom: 24,
    boxShadow: '0 8px 24px rgba(95,61,196,0.20)',
  },
  heroLabel: {
    fontSize: 12, fontWeight: 700, opacity: 0.85,
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10,
  },
  heroAmount: { fontSize: 44, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 },
  heroSub: {
    marginTop: 12, fontSize: 13, opacity: 0.85,
    display: 'flex', justifyContent: 'space-between',
  },
  autoApplyRow: {
    marginTop: 16, paddingTop: 14,
    borderTop: '1px solid rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  },
  autoApplyLabel: {
    fontSize: 13, fontWeight: 500, opacity: 0.9,
  },
  promoSection: { marginBottom: 24 },
  promoLabel: {
    fontSize: 12, fontWeight: 700, color: colors.gray[600],
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
  },
  promoRow: { display: 'flex', gap: 8 },
  promoInput: {
    flex: 1, height: 44, borderRadius: 10,
    border: `1px solid ${colors.gray[300]}`, padding: '0 14px',
    fontSize: 15, color: colors.ink.black, background: colors.gray[100],
    outline: 'none',
  },
  promoBtn: {
    height: 44, padding: '0 18px', borderRadius: 10, border: 'none',
    cursor: 'pointer', background: '#5F3DC4', color: '#fff',
    fontSize: 15, fontWeight: 600,
  },
  historySection: {},
  historyLabel: {
    fontSize: 12, fontWeight: 700, color: colors.gray[600],
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 12,
  },
  emptyHistory: {
    padding: 24, textAlign: 'center', color: colors.gray[600], fontSize: 13,
  },
  historyList: {
    background: colors.white, borderRadius: 14,
    border: `1px solid ${colors.gray[300]}`, overflow: 'hidden',
  },
  historyRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 16px',
    borderBottom: `0.5px solid ${colors.gray[100]}`,
  },
  historyIcon: {
    width: 36, height: 36, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  historyBody: { flex: 1, minWidth: 0 },
  historyDesc: { fontSize: 15, fontWeight: 500, color: colors.ink.black },
  historyDate: { fontSize: 12, color: colors.gray[600], marginTop: 2 },
  historyAmount: { fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' },
};
